<script>
import { useAlert } from 'dashboard/composables';
import Logo from './Logo.vue';
import PrimaryNavItem from './PrimaryNavItem.vue';
import OptionsMenu from './OptionsMenu.vue';
import AgentDetails from './AgentDetails.vue';
import NotificationBell from './NotificationBell.vue';
import wootConstants from 'dashboard/constants/globals';
import { frontendURL } from 'dashboard/helper/URLHelper';
import { ACCOUNT_EVENTS } from '../../../helper/AnalyticsHelper/events';
import TicketNavItem from './TicketNavItem.vue';
import { mapActions, mapGetters } from 'vuex';
import { buildSimAppUrl, getBrandSimAppUrl } from 'dashboard/helpers/simAppUrl';
import { hasAiStudioAccess } from 'dashboard/helper/subscriptionHelper';

export default {
  components: {
    Logo,
    PrimaryNavItem,
    OptionsMenu,
    AgentDetails,
    NotificationBell,
    TicketNavItem,
  },
  props: {
    isACustomBrandedInstance: {
      type: Boolean,
      default: false,
    },
    logoSource: {
      type: String,
      default: '',
    },
    installationName: {
      type: String,
      default: '',
    },
    accountId: {
      type: Number,
      default: 0,
    },
    menuItems: {
      type: Array,
      default: () => [],
    },
    activeMenuItem: {
      type: String,
      default: '',
    },
  },
  computed: {
    ...mapGetters('stripe', ['getBillingUrl', 'getStudioUrl', 'getModuleId']),
    ...mapGetters({ globalConfig: 'globalConfig/get' }),
    // Derived from the request domain (see dashboard_controller.rb), not the account's own
    // custom_attributes — accounts can predate/skip that field, but the domain is always
    // authoritative for which brand's SimAI/AI Studio URL to open.
    accountSource() {
      return this.globalConfig.accountSource;
    },
    simAppUrl() {
      return getBrandSimAppUrl(this.accountSource) || '';
    },
    isCondoManagementModule() {
      return this.getModuleId === 'condo_management';
    },
    canAccessAiStudio() {
      return hasAiStudioAccess(this.$store);
    },
    simAppTooltip() {
      return this.canAccessAiStudio
        ? this.$t('SIDEBAR.SIM')
        : this.$t('SIDEBAR.SIM_RESTRICTED');
    },
    settingsUrl() {
      return this.frontendURL(`accounts/${this.accountId}/settings`);
    },
  },
  data() {
    return {
      helpDocsURL: wootConstants.DOCS_URL,
      showOptionsMenu: false,
    };
  },
  methods: {
    ...mapActions('stripe', ['fetchStripeSession', 'fetchStudioSession']),
    frontendURL,
    toggleOptions() {
      this.showOptionsMenu = !this.showOptionsMenu;
    },
    toggleAccountModal() {
      this.$emit('toggleAccounts');
    },
    toggleSupportChatWindow() {
      window.$chatwoot.toggle();
    },
    openNotificationPanel() {
      this.$track(ACCOUNT_EVENTS.OPENED_NOTIFICATIONS);
      this.$emit('openNotificationPanel');
    },
    async fetchBillingUrl() {
      try {
        await this.fetchStripeSession();
        window.location.href = `//${this.getBillingUrl}`;
      } catch (err) {
        console.error(err);
        this.$router.push({ name: 'pricing_table' });
      }
    },
    async fetchStudioUrl() {
      try {
        await this.fetchStudioSession();
        window.location.href = `//${this.getStudioUrl}`;
      } catch (err) {
        console.error(err);
        useAlert('Failed to access Studio. Please try again later.');
      }
    },
    openSimApp() {
      if (!this.canAccessAiStudio) return;
      const url = buildSimAppUrl({
        accountId: this.accountId,
        accountSource: this.accountSource,
      });
      if (!url) return;
      window.open(url, '_blank');
    },
  },
  mounted() {
    this.fetchStripeSession();
  },
};
</script>

<template>
  <div
    class="flex flex-col justify-between w-16 h-full bg-white border-r dark:bg-slate-900 border-slate-50 dark:border-slate-800/50 rtl:border-l rtl:border-r-0"
  >
    <div class="flex flex-col items-center">
      <Logo
        :source="logoSource"
        :name="installationName"
        :account-id="accountId"
        class="m-4 mb-5"
      />
      <PrimaryNavItem
        v-for="menuItem in menuItems"
        :key="menuItem.toState"
        :icon="menuItem.icon"
        :name="menuItem.label"
        :to="menuItem.toState"
        :is-child-menu-active="menuItem.key === activeMenuItem"
        :open-in-new-page="menuItem.openInNewPage"
        :inactive-on-routes="menuItem.inactiveOnRoutes"
      />
    </div>
    <div class="flex flex-col items-center justify-end pb-2">
      <button
        v-if="simAppUrl && !isCondoManagementModule"
        v-tooltip.right-end="simAppTooltip"
        :disabled="!canAccessAiStudio"
        class="text-slate-700 dark:text-slate-100 w-10 h-10 my-2 flex items-center justify-center rounded-lg hover:bg-slate-25 dark:hover:bg-slate-700 dark:hover:text-slate-100 hover:text-slate-600 relative"
        :class="{
          'opacity-40 cursor-not-allowed hover:bg-transparent dark:hover:bg-transparent':
            !canAccessAiStudio,
        }"
        @click="openSimApp"
      >
        <fluent-icon icon="sim-ai" />
        <span class="sr-only">SIM</span>
      </button>

      <!-- <button
        @click="fetchBillingUrl"
        v-tooltip.right-end="$t(`SIDEBAR.BILLING`)"
        class="text-slate-700 dark:text-slate-100 w-10 h-10 my-2 flex items-center justify-center rounded-lg hover:bg-slate-25 dark:hover:bg-slate-700 dark:hover:text-slate-100 hover:text-slate-600 relative"
      >
        <fluent-icon icon="credit-card-person" />
        <span class="sr-only">BILLING</span>
      </button> -->

      <PrimaryNavItem
        key="settings"
        icon="settings"
        name="SETTINGS"
        :to="settingsUrl"
        :is-child-menu-active="activeMenuItem === 'settings'"
        :open-in-new-page="false"
      />

      <!-- <TicketNavItem /> -->
      <NotificationBell @openNotificationPanel="openNotificationPanel" />
      <AgentDetails @toggleMenu="toggleOptions" />
      <OptionsMenu
        :show="showOptionsMenu"
        @toggleAccounts="toggleAccountModal"
        @showSupportChatWindow="toggleSupportChatWindow"
        @openKeyShortcutModal="$emit('openKeyShortcutModal')"
        @close="toggleOptions"
      />
    </div>
  </div>
</template>
